import { useState, useEffect } from "react";
import { auth, db, storage } from "../firebase/config";
import { useNavigate } from "react-router-dom";

import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  deleteDoc,
  setDoc,
  doc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

export default function EmployerOnboarding() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [taskInputs, setTaskInputs] = useState({});
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");

  // ✅ Fetch Company ID once user is authenticated
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          setLoading(true);
          setError(""); // ✅ Clear any previous errors

          const employerRef = doc(db, "users", user.uid);
          const employerDoc = await getDoc(employerRef);

          if (!employerDoc.exists() || !employerDoc.data().companyId) {
            throw new Error("Company ID not found.");
          }

          setCompanyId(employerDoc.data().companyId);
        } catch (err) {
          console.error("Error fetching company ID:", err.message);
          setError("Company information is missing. Please contact support.");
        } finally {
          setLoading(false);
        }
      } else {
        setError("Employer not authenticated.");
        setCompanyId(null); // Clear companyId to avoid issues
        setLoading(false);
      }
    });

    return () => unsubscribe(); // ✅ Cleanup listener on unmount
  }, []);

  // ✅ Fetch Employees when `companyId` is available
  useEffect(() => {
    if (!companyId) return; // ✅ Only fetch if companyId is available

    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const employeesRef = collection(db, `companies/${companyId}/employees`);
        const querySnapshot = await getDocs(employeesRef);
        const employeeList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setEmployees(employeeList); // ✅ Update employees state
      } catch (err) {
        console.error("Error fetching employees:", err.message);
        setError("Failed to fetch employees.");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [companyId]);

  const handleAssignCategory = async (employeeId, category) => {
    try {
      const categoryRef = doc(
        db,
        `companies/${companyId}/employees/${employeeId}/onboarding_progress`,
        category.id
      );

      const existingCategory = await getDoc(categoryRef);
      if (existingCategory.exists()) {
        alert(`Category "${category.categoryName}" is already assigned.`);
        return;
      }

      const formattedTasks = category.tasks.map((task) => ({
        taskId: task.id,
        taskName: task.taskName,
        taskType: task.taskType, // Include task type
        status: "pending",
        assignedFields: task.fields
          ? task.fields.reduce((acc, field) => {
              acc[field] = "";
              return acc;
            }, {})
          : {},
        fileUrl: task.fileUrl || null, // Include file URL if it's a Document Task
      }));

      await setDoc(categoryRef, {
        categoryName: category.categoryName,
        tasks: formattedTasks,
      });

      alert(`Category "${category.categoryName}" assigned to ${employeeId}.`);
      setSelectedEmployee("");
      setTaskInputs({});
      setNewCategory("");

      navigate("/employer-assigned-task");
    } catch (err) {
      console.error("Error assigning category:", err.message);
      setError("Failed to assign category.");
    }
  };

  useEffect(() => {
    setSelectedEmployee(""); // Reset selection on component mount
  }, []);

  useEffect(() => {
    const fetchCompanyId = async () => {
      try {
        setLoading(true);
        const employer = auth.currentUser;
        if (!employer) throw new Error("Employer not authenticated.");

        const employerRef = doc(db, "users", employer.uid);
        const employerDoc = await getDoc(employerRef);

        if (!employerDoc.exists() || !employerDoc.data().companyId) {
          throw new Error("Company ID not found.");
        }

        setCompanyId(employerDoc.data().companyId);
      } catch (err) {
        console.error("Error fetching company ID:", err.message);
        setError("Company information is missing. Please contact support.");
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyId();
  }, []);

  // ✅ Add a New Category
  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      const categoryRef = await addDoc(
        collection(db, `companies/${companyId}/onboarding_templates`),
        { categoryName: newCategory }
      );
      setCategories([
        ...categories,
        { id: categoryRef.id, categoryName: newCategory, tasks: [] },
      ]);
      setNewCategory("");
    } catch (err) {
      console.error("Error adding category:", err.message);
      setError("Failed to add category.");
    }
  };

  // ✅ Delete a Category
  const handleDeleteCategory = async (categoryId) => {
    try {
      await deleteDoc(
        doc(db, `companies/${companyId}/onboarding_templates`, categoryId)
      );
      setCategories(categories.filter((cat) => cat.id !== categoryId));
    } catch (err) {
      console.error("Error deleting category:", err.message);
      setError("Failed to delete category.");
    }
  };

  // ✅ Handle Task Input Change
  const handleTaskInputChange = (categoryId, field, value) => {
    setTaskInputs({
      ...taskInputs,
      [categoryId]: { ...taskInputs[categoryId], [field]: value },
    });
  };

  // Handle File Upload to Firebase Storage
  const handleFileUpload = async (file) => {
    if (!file) return null;

    const fileRef = ref(storage, `onboarding_docs/${file.name}`);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  };

  // ✅ Handle Form Field Addition (Fixed!)
  const handleAddField = (categoryId, taskId) => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? {
              ...cat,
              tasks: cat.tasks.map((task) =>
                task.id === taskId
                  ? {
                      ...task,
                      fields: [
                        ...new Set([...(task.fields || []), "New Field"]),
                      ],
                    }
                  : task
              ),
            }
          : cat
      )
    );
  };

  // ✅ Add a Task to a Category
  const handleAddTask = async (categoryId) => {
    const taskDetails = taskInputs[categoryId] || {};
    if (!taskDetails.taskName) return;

    let fileUrl = null;
    if (taskDetails.taskType === "document_task" && taskDetails.file) {
      fileUrl = await handleFileUpload(taskDetails.file);
    }

    const newTask = {
      taskName: taskDetails.taskName,
      taskType: taskDetails.taskType,
      required: taskDetails.required || false,
      fields: taskDetails.taskType === "form_entry" ? [] : null, // Only store fields for form entry
      fileUrl: fileUrl, // Store file URL if it's a document task
    };

    try {
      const taskRef = await addDoc(
        collection(
          db,
          `companies/${companyId}/onboarding_templates/${categoryId}/tasks`
        ),
        newTask
      );

      setCategories((prev) =>
        prev.map((cat) =>
          cat.id === categoryId
            ? {
                ...cat,
                tasks: [...cat.tasks, { id: taskRef.id, ...newTask }],
              }
            : cat
        )
      );

      setTaskInputs({ ...taskInputs, [categoryId]: {} });
    } catch (err) {
      console.error("Error adding task:", err.message);
      setError("Failed to add task.");
    }
  };

  // ✅ Delete a Task
  const handleDeleteTask = async (categoryId, taskId) => {
    try {
      await deleteDoc(
        doc(
          db,
          `companies/${companyId}/onboarding_templates/${categoryId}/tasks`,
          taskId
        )
      );
      setCategories((prev) =>
        prev.map((cat) =>
          cat.id === categoryId
            ? { ...cat, tasks: cat.tasks.filter((task) => task.id !== taskId) }
            : cat
        )
      );
    } catch (err) {
      console.error("Error deleting task:", err.message);
      setError("Failed to delete task.");
    }
  };

  // ✅ Delete a Form Field
  const handleDeleteField = (categoryId, taskId, fieldIndex) => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? {
              ...cat,
              tasks: cat.tasks.map((task) =>
                task.id === taskId
                  ? {
                      ...task,
                      fields: task.fields.filter(
                        (_, index) => index !== fieldIndex
                      ),
                    }
                  : task
              ),
            }
          : cat
      )
    );
  };

  if (loading) {
    return <p>Checking authentication...</p>;
  }

  if (!companyId) {
    return <p style={{ color: "red" }}>Employer not authenticated.</p>;
  }

  return (
    <div>
      <h1>Employer Onboarding Setup</h1>
      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p style={{ color: "red" }}>{error}</p>
      ) : (
        <>
          <h3>Assign Task to Employee</h3>
          {selectedEmployee && (
            <p>
              Assigning to:{" "}
              {employees.find((emp) => emp.id === selectedEmployee)?.name}
            </p>
          )}
          <select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            <option value="">Select an Employee</option>
            {employees.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.name}
              </option>
            ))}
          </select>

          <h2>Onboarding Categories & Tasks</h2>
          {categories.map((category) => (
            <div key={category.id} style={{ marginBottom: "20px" }}>
              <h3>{category.categoryName}</h3>
              <button
                onClick={() => handleAssignCategory(selectedEmployee, category)}
                disabled={!selectedEmployee || category.tasks.length === 0} // Prevent empty assignments
              >
                📂 Assign Category to Employee
              </button>
              <button onClick={() => handleDeleteCategory(category.id)}>
                🗑 Delete
              </button>
              <ul>
                {category.tasks.map((task) => (
                  <li key={task.id}>
                    {task.taskName} {task.required ? "(Required)" : ""}
                    <button
                      onClick={() => handleDeleteTask(category.id, task.id)}
                    >
                      🗑 Delete Task
                    </button>
                    <div>
                      <h5>Form Fields</h5>
                      {task.fields?.map((field, index) => (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <input
                            type="text"
                            placeholder="Field Name"
                            value={field}
                            onChange={(e) =>
                              setCategories((prev) =>
                                prev.map((cat) =>
                                  cat.id === category.id
                                    ? {
                                        ...cat,
                                        tasks: cat.tasks.map((t) =>
                                          t.id === task.id
                                            ? {
                                                ...t,
                                                fields: t.fields.map((f, i) =>
                                                  i === index
                                                    ? e.target.value
                                                    : f
                                                ),
                                              }
                                            : t
                                        ),
                                      }
                                    : cat
                                )
                              )
                            }
                          />
                          <button
                            onClick={() =>
                              handleDeleteField(category.id, task.id, index)
                            }
                          >
                            🗑 Delete Field
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => handleAddField(category.id, task.id)}
                      >
                        ➕ Add Field
                      </button>
                    </div>
                  </li>
                ))}
              </ul>

              <h4>Add Task</h4>
              <input
                type="text"
                placeholder="Task Name"
                value={taskInputs[category.id]?.taskName || ""}
                onChange={(e) =>
                  handleTaskInputChange(category.id, "taskName", e.target.value)
                }
              />
              <select
                value={taskInputs[category.id]?.taskType || ""}
                onChange={(e) =>
                  handleTaskInputChange(category.id, "taskType", e.target.value)
                }
              >
                <option value="">Select Task Type</option>
                <option value="form_entry">Form Entry Task</option>
                <option value="document_task">Document Task</option>
              </select>
              {taskInputs[category.id]?.taskType === "document_task" && (
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) =>
                    handleTaskInputChange(
                      category.id,
                      "file",
                      e.target.files[0]
                    )
                  }
                />
              )}
              <button onClick={() => handleAddTask(category.id)}>
                ➕ Add Task
              </button>
            </div>
          ))}

          <h3>Add New Category</h3>
          <input
            type="text"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            placeholder="New Category Name"
          />
          <button onClick={handleAddCategory}>➕ Add Category</button>
        </>
      )}
      <div style={{ marginBottom: "20px" }}>
        <button onClick={() => navigate("/employer-assigned-task")}>
          📋 View Assigned Tasks
        </button>
      </div>
    </div>
  );
}
